import * as XLSX from 'xlsx';
import { format } from 'date-fns';
import { SchedulingData, SchedulingReporter } from '../types';
import { HourSlotsEncoder } from './hourSlotsEncoder';

export const exportSchedulingToExcel = (schedulingData: SchedulingData, weekStart: Date) => {
  const workbook = XLSX.utils.book_new();
  
  // Get all unique states from reporters
  const allStates = new Set<string>();
  schedulingData.reporters.forEach(reporter => {
    reporter.statePreferences.forEach(pref => {
      allStates.add(pref.state);
    });
  });
  
  const sortedStates = Array.from(allStates).sort();
  
  // Create a worksheet for each state
  sortedStates.forEach(state => {
    const reportersForState = schedulingData.reporters.filter(reporter =>
      reporter.statePreferences.some(pref => pref.state === state)
    );
    
    if (reportersForState.length === 0) return;
    
    // Create worksheet data
    const worksheetData: any[][] = [];
    
    // Header row
    const headers = [
      'Reporter Name',
      'Location', 
      'Email',
      'Monday',
      'Tuesday', 
      'Wednesday',
      'Thursday',
      'Friday'
    ];
    worksheetData.push(headers);
    
    // Data rows
    reportersForState.forEach(reporter => {
      // Find the state preference for the current state being exported
      const statePreference = reporter.statePreferences.find(pref => pref.state === state);
      const virtualAllowed = statePreference?.virtualAvailable ?? true;
      const inPersonAllowed = statePreference?.inPersonAvailable ?? true;
      
      const row = [
        reporter.name || `Reporter ${reporter.reporterId}`,
        reporter.location || '',
        reporter.email || '',
        formatAvailability(reporter, 'monday', virtualAllowed, inPersonAllowed),
        formatAvailability(reporter, 'tuesday', virtualAllowed, inPersonAllowed),
        formatAvailability(reporter, 'wednesday', virtualAllowed, inPersonAllowed),
        formatAvailability(reporter, 'thursday', virtualAllowed, inPersonAllowed),
        formatAvailability(reporter, 'friday', virtualAllowed, inPersonAllowed)
      ];
      worksheetData.push(row);
    });
    
    // Create worksheet
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    
    // Set column widths
    worksheet['!cols'] = [
      { wch: 20 }, // Reporter Name
      { wch: 15 }, // Location
      { wch: 25 }, // Email
      { wch: 15 }, // Monday
      { wch: 15 }, // Tuesday
      { wch: 15 }, // Wednesday
      { wch: 15 }, // Thursday
      { wch: 15 }  // Friday
    ];
    
    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, state);
  });
  
  // Add "No State" tab for reporters without state preferences
  const reportersWithNoState = schedulingData.reporters.filter(reporter =>
    !reporter.statePreferences || reporter.statePreferences.length === 0
  );

  if (reportersWithNoState.length > 0) {
    const worksheetData: any[][] = [];

    // Header row
    const headers = [
      'Reporter Name',
      'Location',
      'Email',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday'
    ];
    worksheetData.push(headers);

    // Data rows - no state preference masking (show full availability)
    reportersWithNoState.forEach(reporter => {
      const row = [
        reporter.name || `Reporter ${reporter.reporterId}`,
        reporter.location || '',
        reporter.email || '',
        formatAvailability(reporter, 'monday', true, true),
        formatAvailability(reporter, 'tuesday', true, true),
        formatAvailability(reporter, 'wednesday', true, true),
        formatAvailability(reporter, 'thursday', true, true),
        formatAvailability(reporter, 'friday', true, true)
      ];
      worksheetData.push(row);
    });

    // Create worksheet
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    // Set column widths
    worksheet['!cols'] = [
      { wch: 20 }, // Reporter Name
      { wch: 15 }, // Location
      { wch: 25 }, // Email
      { wch: 15 }, // Monday
      { wch: 15 }, // Tuesday
      { wch: 15 }, // Wednesday
      { wch: 15 }, // Thursday
      { wch: 15 }  // Friday
    ];

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'No State');
  }

  // If no states found, create a summary sheet
  if (sortedStates.length === 0 && reportersWithNoState.length === 0) {
    const summaryData = [
      ['No scheduling data available for the selected criteria'],
      [''],
      ['Week:', format(weekStart, 'MMM dd, yyyy')],
      ['Reporters:', schedulingData.reporters.length.toString()]
    ];

    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');
  }
  
  // Generate filename
  const weekFormatted = format(weekStart, 'yyyy-MM-dd');
  const filename = `Reporter_Scheduling_${weekFormatted}.xlsx`;
  
  // Save file
  XLSX.writeFile(workbook, filename);
};

const formatAvailability = (
  reporter: SchedulingReporter,
  day: keyof typeof reporter.weekAvailability,
  virtualAllowed: boolean = true,
  inPersonAllowed: boolean = true
): string => {
  const dayAvailability = reporter.weekAvailability[day];
  
  if (!dayAvailability || !dayAvailability.isAvailable) {
    // Check if explicitly unavailable vs empty
    if (dayAvailability?.unavailable) {
      // Explicitly marked as unavailable
      const notes = dayAvailability.notes ? ` (${dayAvailability.notes})` : '';
      return `Unavailable${notes}`;
    } else if (dayAvailability?.empty) {
      // Empty - no record, just show notes if present
      return dayAvailability.notes || '';
    }
    // Fallback for backward compatibility
    return 'Unavailable';
  }
  
  // Get time blocks if hour slots are available
  let timeBlocks = dayAvailability.timeBlocks || 
    (dayAvailability.hourSlots ? HourSlotsEncoder.getTimeBlocks(dayAvailability.hourSlots) : []);
  
  // Apply state preference mask to time blocks
  if (timeBlocks.length > 0) {
    timeBlocks = HourSlotsEncoder.applyStatePreferenceMaskToBlocks(
      timeBlocks,
      virtualAllowed,
      inPersonAllowed
    );
  }
  
  let formattedAvailability = '';
  
  if (timeBlocks.length > 0) {
    // Format time blocks with job types
    formattedAvailability = timeBlocks.map(block => {
      const startTime = formatTime(block.startTime);
      const endTime = formatTime(block.endTime);
      
      let jobTypeIndicator = '';
      const hasVirtual = block.jobTypes.includes('virtual');
      const hasInPerson = block.jobTypes.includes('in-person');
      
      if (hasVirtual && hasInPerson) {
        jobTypeIndicator = ''; // ' V-IP' can be omitted for brevity
      } else if (hasVirtual) {
        jobTypeIndicator = ' V';
      } else if (hasInPerson) {
        jobTypeIndicator = ' IP';
      }
      
      return `${startTime}-${endTime}${jobTypeIndicator}`;
    }).join(', ');
  } else {
    // Fallback to simple time range
    const jobTypes = dayAvailability.jobTypes || [];
    
    // Filter job types based on state preferences
    const allowedJobTypes = jobTypes.filter((jobType: string) => {
      if (jobType === 'virtual') return virtualAllowed;
      if (jobType === 'in-person') return inPersonAllowed;
      return false;
    });
    
    if (allowedJobTypes.length === 0 && jobTypes.length > 0) {
      // Had availability but none allowed for this state
      return '';
    }
    
    const hasVirtual = allowedJobTypes.includes('virtual');
    const hasInPerson = allowedJobTypes.includes('in-person');
    
    let jobTypeIndicator = '';
    if (hasVirtual && hasInPerson) {
      jobTypeIndicator = ''; // ' V-IP' can be omitted for brevity
    } else if (hasVirtual) {
      jobTypeIndicator = ' V';
    } else if (hasInPerson) {
      jobTypeIndicator = ' IP';
    }
    
    const timeRange = dayAvailability.startTime && dayAvailability.endTime 
      ? `${formatTime(dayAvailability.startTime)}-${formatTime(dayAvailability.endTime)}`
      : 'Available';
    
    formattedAvailability = `${timeRange}${jobTypeIndicator}`;
  }
    
  const notes = dayAvailability.notes ? ` *${dayAvailability.notes}` : '';
  
  return `${formattedAvailability}${notes}`;
};

const formatTime = (timeStr: string): string => {
  if (!timeStr) return '';
  
  // timeStr comes as "HH:MM:SS", convert to 12-hour format
  const [hours, minutes] = timeStr.split(':');
  let hour = parseInt(hours, 10);
  const min = parseInt(minutes, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  
  // Convert to 12-hour format
  if (hour === 0) hour = 12; // midnight
  else if (hour > 12) hour = hour - 12; // PM hours
  
  if (min === 0) {
    return `${hour}${ampm}`;
  } else {
    return `${hour}:${minutes.padStart(2, '0')}${ampm}`;
  }
};